import { useForm } from "antd/lib/form/Form";
import { Form, Input, Col, Row, Spin, Alert, message } from "antd";
import { useTranslate } from "@refinedev/core";
import { CheckOutlined } from "@ant-design/icons";
import { Flex, Button, theme } from "antd";
import {
  useJsApiLoader,
  Autocomplete,
  GoogleMap,
  MarkerF,
} from "@react-google-maps/api";
import { PhoneNumberInput } from "@/components/phone-number-input";
import xconsMap from "@/assets/img/xcons-map.svg";
import { useEffect, useRef, useState } from "react";
import { API_URL_ORDERS, VITE_GOOGLE_MAPS_API_KEY } from "@/services/XCONS_ENV";
import { axiosInstance } from "@/utilities";

const API_KEY = VITE_GOOGLE_MAPS_API_KEY;

const libraries: "places"[] = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: "200px",
  borderRadius: "8px",
  marginBottom: "16px",
  border: "1px solid #d9d9d9",
};

const defaultCenter = {
  lat: -34.603722,
  lng: -58.381592,
};

const { TextArea } = Input;

export const DeliveryAddress = ({
  address,
  updateOrderData,
  orderId,
  closeModal,
}: {
  address: any;
  orderId: string;
  updateOrderData?: () => void;
  closeModal?: () => void;
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const [locationData, setLocationData] = useState<{
    latitude: any;
    longitude: any;
    countryId: string;
  }>({
    latitude: "-34.603722",
    longitude: "-58.381592",
    countryId: "AR",
  });

  const [form] = useForm();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markerLoaded, setMarkerLoaded] = useState(false);
  const [loadingAddressUpdate, setLoadingAddressUpdate] = useState(false);

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: API_KEY,
    libraries,
    language: "es",
    region: "AR",
    version: "weekly",
  });

  useEffect(() => {
    if (address) {
      form.setFieldsValue(address);
      setLocationData({
        latitude: address.latitude || "-34.603722",
        longitude: address.longitude || "-58.381592",
        countryId: address.countryId || "AR",
      });
    }
  }, [address]);

  useEffect(() => {
    if (loadError) {
      setError("Error loading Google Maps API");
      setLoading(false);
    } else if (isLoaded) {
      setLoading(false);
      setError(null);
    }
  }, [isLoaded, loadError]);

  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    if (!place.geometry?.location) return;

    const lat = place.geometry.location.lat().toString();
    const lng = place.geometry.location.lng().toString();

    const addressComponents = place.address_components || [];

    const getComponent = (type: string) =>
      addressComponents.find((c) => c.types.includes(type))?.long_name;

    const getShortComponent = (type: string) =>
      addressComponents.find((c) => c.types.includes(type))?.short_name;

    const fullAddress = [getComponent("route"), getComponent("street_number")]
      .filter(Boolean)
      .join(", ");

    form.setFieldsValue({
      street: fullAddress,
      city: getComponent("locality"),
      region: getComponent("administrative_area_level_1"),
      country: getComponent("country"),
      postcode: getComponent("postal_code"),
    });

    setLocationData({
      latitude: lat,
      longitude: lng,
      countryId: getShortComponent("country") || "AR",
    });
  };

  const onAutocompleteLoad = (
    autocomplete: google.maps.places.Autocomplete,
  ) => {
    if (!window.google) return;

    const bounds = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(-55.174989, -73.563892),
      new window.google.maps.LatLng(-21.770833, -53.984375),
    );

    autocomplete.setBounds(bounds);
    autocomplete.setComponentRestrictions({ country: ["AR"] });

    autocompleteRef.current = autocomplete;

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place && place.address_components) {
        handlePlaceSelect(place);
      }
    });
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const updatedAddress = {
        ...address,
        ...values,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        countryId: locationData.countryId,
      };
      handleAddressUpdated(updatedAddress);
    });
  };

  const handleAddressUpdated = async (updatedAddress: any) => {
    setLoadingAddressUpdate(true);

    const payload: any = {
      firstname: updatedAddress.firstname,
      lastname: updatedAddress.lastname,
      telephone: updatedAddress.telephone,
      city: updatedAddress.city,
      countryId: updatedAddress.countryId,
      latitude: updatedAddress.latitude,
      longitude: updatedAddress.longitude,
      postcode: updatedAddress.postcode,
      region: updatedAddress.region,
      shippingIndication: updatedAddress.shippingIndication,
      street: updatedAddress.street,
    };

    try {
      await axiosInstance.patch(
        `${API_URL_ORDERS}/${orderId}/address/${"shipping"}`,
        payload,
      );

      message.success(t("common.messages.addressUpdated"));
      updateOrderData?.();
      closeModal?.();
    } catch (error) {
      console.error(error);
      message.error("Error updating address");
    } finally {
      setLoadingAddressUpdate(false);
    }
  };

  if (loadError) return <div>Error al cargar Google Maps</div>;
  if (!isLoaded) return <Spin spinning />;

  return (
    <Spin spinning={loadingAddressUpdate}>
      <Flex vertical style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        <Form form={form} style={{ width: "100%" }} layout="vertical">
          <Spin spinning={false}>
            <Row style={{ marginBottom: 20 }}>
              <Col xs={24} md={24} lg={24}>
                <Flex>
                  <Alert
                    style={{ width: "100%" }}
                    message={t("orders.delivery.warning.recalculate")}
                    type="warning"
                    showIcon
                    closable
                  />
                </Flex>
              </Col>
            </Row>
            <Row gutter={[48, 0]} justify="start">
              <Col xs={24} md={12} lg={12}>
                <Flex gap={8} justify="space-between">
                  <Form.Item
                    label={t("products.show.tableName")}
                    name="firstname"
                    initialValue={address?.firstname}
                  >
                    <Input
                      placeholder={t(
                        "orders.placeholder.enternameofthepersonreceivingtheorder",
                      )}
                    />
                  </Form.Item>

                  <Form.Item
                    label={t("lastname")}
                    name="lastname"
                    initialValue={address?.lastname}
                  >
                    <Input />
                  </Form.Item>
                </Flex>

                <Form.Item
                  label="Whatsapp"
                  name="telephone"
                  initialValue={address?.telephone}
                  rules={[
                    {
                      required: true,
                      message: t(
                        "contacts.address.validation.whatsapprequired",
                      ),
                    },
                    {
                      validator: (_, value) => {
                        const phoneDigits = value?.replace(/\D/g, "");
                        if (!phoneDigits || phoneDigits.length < 11) {
                          return Promise.reject(
                            t("contacts.address.validation.whatsapplength"),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <PhoneNumberInput
                    country={address?.countryId?.toLowerCase() || "ar"}
                  />
                </Form.Item>

                <Form.Item
                  label={t("pages.login.fields.email")}
                  name="email"
                  initialValue={address?.email}
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input
                    placeholder={t(
                      "orders.placeholder.searchforstreetandnumber",
                    )}
                  />
                </Form.Item>

                <Autocomplete onLoad={onAutocompleteLoad}>
                  <Form.Item
                    label={t("contacts.label.address")}
                    name="street"
                    rules={[
                      {
                        required: true,
                        message: t("contacts.address.validation.street"),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t("contacts.address.placeholder.details")}
                    />
                  </Form.Item>
                </Autocomplete>

                <Form.Item
                  label={t("contacts.label.state")}
                  name="region"
                  initialValue={address?.region}
                >
                  <Input
                    placeholder={t("contacts.address.placeholder.state")}
                  />
                </Form.Item>

                <Form.Item
                  label={t("contacts.label.city")}
                  name="city"
                  initialValue={address?.city}
                >
                  <Input placeholder={t("contacts.address.placeholder.city")} />
                </Form.Item>

                <Form.Item
                  label={t("create.seller.labelZipCode")}
                  name="postcode"
                  initialValue={address?.postcode}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={12}>
                <Form.Item
                  label={t("create.seller.labelExtraInfo")}
                  name="shippingIndication"
                >
                  <Input.TextArea
                    rows={4}
                    placeholder={t(
                      "contacts.placeholder.instructionsfordelivery",
                    )}
                  />
                </Form.Item>

                <Flex
                  vertical
                  justify="end"
                  align="end"
                  style={{ marginBottom: "24px", width: "100%" }}
                >
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={{
                      lat: parseFloat(locationData.latitude),
                      lng: parseFloat(locationData.longitude),
                    }}
                    zoom={15}
                    onLoad={(map) => {
                      setMap(map);
                      setLoading(false);
                      setMarkerLoaded(true);
                    }}
                    options={{
                      disableDefaultUI: true,
                      zoomControl: true,
                      styles: [
                        {
                          featureType: "poi",
                          elementType: "labels",
                          stylers: [{ visibility: "off" }],
                        },
                      ],
                    }}
                  >
                    <MarkerF
                      position={{
                        lat: parseFloat(locationData.latitude),
                        lng: parseFloat(locationData.longitude),
                      }}
                      options={{
                        animation: window.google.maps.Animation.DROP,
                      }}
                      icon={{
                        url: xconsMap,
                        scaledSize: new window.google.maps.Size(45, 45),
                      }}
                    />
                  </GoogleMap>
                </Flex>
              </Col>
            </Row>
          </Spin>
        </Form>
        </div>
        <Flex
          align="center"
          justify="end"
          gap="8px"
          style={{ flexShrink: 0, padding: "12px 24px", borderTop: "1px solid #f0f0f0", backgroundColor: "#fff" }}
        >
          <Button onClick={closeModal} danger>
            {t("buttons.cancel")}
          </Button>
          <Button
            type="primary"
            icon={<CheckOutlined />} iconPosition="end"
            onClick={handleSubmit}
          >
            {t("buttons.save")}
          </Button>
        </Flex>
      </Flex>
    </Spin>
  );
};
